"use client"

import type React from "react"
import { useEffect, useState, useRef } from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "redux/hooks"
import { getAndSetSellerById } from "redux/sellers/actions"
import { ISellerData } from "redux/sellers/types"
import { ReactComponent as ShowListIcon } from "assets/icons/new/UI/ShowList.svg";
import { ReactComponent as AddIcon } from "assets/icons/new/UI/Add.svg";

interface SellerSelectorProps {
  className?: string
}

const SellerSelector: React.FC<SellerSelectorProps> = ({ className = "" }) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [sellers, setSellers] = useState<ISellerData[]>([])
  const [currentSellerData, setCurrentSellerData] = useState<ISellerData | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { allSellers, currentSeller, isLoading } = useAppSelector(({ sellers }) => {
    return {
      allSellers: sellers.allSellers,
      currentSeller: sellers.currentSeller,
      isLoading: sellers.currentSeller.info.isLoading,
    };
  });

  useEffect(() => {
    if (!allSellers) return
    const lower = searchTerm.toLowerCase()
    const filtered = searchTerm
      ? allSellers.filter(s =>
          s.sellerName?.toLowerCase().includes(lower) ||
          String(s.id).includes(searchTerm)
        )
      : allSellers
    setSellers(filtered)
  }, [searchTerm, allSellers])

  useEffect(() => {
    if(currentSeller?.info && currentSeller.info.data) {
        const mappedCurrentSellerData = {
            ...currentSeller.info.data,
            label: `${currentSeller.info.data.id} - ${currentSeller.info.data.sellerName}`
        }
        setCurrentSellerData(mappedCurrentSellerData);
    }
}, [currentSeller.info])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchTerm("")
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useEffect(() => {
    if (isOpen && searchInputRef.current && window.innerWidth > 1024) {
      searchInputRef.current.focus()
    }
  }, [isOpen])

  const handleChangeCompany = (option: { id: number }) => {
    const prevId = currentSeller.info?.data?.id;
    if(option.id !== prevId) {
      dispatch(getAndSetSellerById(option.id) as any);
      navigate(`/${option.id}`);
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        id="sellerSelectorButton"
        className={`flex items-center space-x-1 hover:text-theme-hover dark:hover:text-theme-hover`}
        disabled={isLoading}
      >
        <span className="text-sm overflow-hidden text-ellipsis whitespace-nowrap max-w-28">
          {currentSellerData?.sellerName || ""}
        </span>
        <div className="flex flex-col ml-[7px]">
          {isLoading ? (
            <div className="w-4 h-4 border border-gray-400 border-t-transparent rounded-full animate-spin" />
          ) : (
            <div className="flex flex-col space-y-[2px]">
              <ShowListIcon width="24" height="24" fill="currentColor" className="shrink-0 size-3.5" />
            </div>
          )}
        </div>
      </button>

      {isOpen && (
        <div className={`absolute top-full left-0 mt-1 w-60 sm:w-80 shadow-2xl hover:text-theme-hover dark:hover:text-theme-hover border border-theme-border dark:border-theme-border bg-theme-bg dark:bg-theme-bg rounded-xl z-150`}>
          {/* Поле поиска */}
          <div className='p-2'>
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                placeholder={t('SearchByCompany')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full h-8.5 px-3 py-2 pr-8 text-sm bg-theme-bg dark:bg-theme-bg dark:text-c-gray dark:placeholder:text-gray-500 border !border-theme-border dark:!border-theme-border rounded-xl focus:outline-none focus:border-2 focus:ring-0`}
              />
              {isLoading && (
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                </div>
              )}
            </div>
          </div>

          {/* Список компаний */}
          <div className="max-h-60 flex flex-col">
            <div  className="overflow-y-auto">
              {sellers.length > 0 ? (
                sellers.map((seller, i) => (
                  <button
                    key={seller.id}
                    onClick={() => handleChangeCompany(seller)}
                    className={`w-full px-3 py-2 text-left text-sm cursor-pointer dark:hover:text-theme-color-active hover:bg-theme-hover dark:hover:bg-theme-hover hover:text-theme-color-active ${currentSellerData?.id === seller.id ? `bg-theme-bg-active dark:bg-theme-bg-active text-theme-color-active dark:text-theme-color-active` : ''}`}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">{seller.id}</span>
                      <span>-</span>
                      <span className="truncate">{seller.sellerName}</span>
                    </div>
                  </button>
                ))
              ) : (
                <div className="px-3 py-4 text-sm text-center">
                  {searchTerm ? "Компании не найдены" : "Нет доступных компаний"}
                </div>
              )}
            </div>
            <div className={`border-t rounded-b-lg border-theme-border dark:border-theme-border hover:bg-theme-hover dark:hover:bg-theme-hover hover:text-theme-color-active dark:hover:text-theme-color-active`}>
              <Link
                target="_blank"
                className={`flex justify-center items-center gap-x-3 p-2 text-sm font-medium rounded-b-lg focus:outline-hidden`}
                to="/seller/new"
              >
                <AddIcon width="20" height="20" fill="currentColor" />
                <span className="overflow-hidden text-ellipsis whitespace-nowrap max-w-60 sm:max-w-80">{t('create_company', { ns: 'messages' })}</span>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SellerSelector
